import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  accessTokenAtom,
  addMessageAtom,
  removeMessagesAtom,
  applyFamilyAppEventAtom,
  gatheringRingAtom,
  setTypingAtom,
  setUserOnlineAtom,
} from '../store';
import {
  WS_EVENTS,
  type WsGatheringRingPayload,
  type WsMessageDeletedPayload,
} from '@twinden/shared';
import { discardForMessages } from '../lib/outbox';
import { api } from '../lib/api';
import { localizeDeletedAccounts } from '../lib/deletedAccounts';
import { notificationConversationId } from '../lib/notificationLinks';

type WsMessage = {
  event: string;
  payload: unknown;
  timestamp: string;
};

export type WsEventHandler = (message: WsMessage) => void;

/**
 * Why the app woke (issue #75): it came to the front (`visible`), came back
 * from the back-forward cache (`pageshow`), got focus, came back online, or a
 * notification was tapped (`notification`, with the chat it opens).
 */
export type WakeReason =
  | 'visible'
  | 'pageshow'
  | 'focus'
  | 'online'
  | 'notification';
export interface Wake {
  reason: WakeReason;
  conversationId?: string;
}
export type WakeHandler = (wake: Wake) => void;

/** No health:pong this long after a wake's ping: the socket is dead (#75). */
export const WAKE_PING_TIMEOUT_MS = 3000;
/** The wait before reconnecting after a socket closes on its own. */
export const RECONNECT_DELAY_MS = 3000;
// The front, pageshow and focus usually arrive together: one catch-up for
// them. Focus alone (a desktop window clicked back into) catches up less
// often. A tapped notification always does.
const WAKE_GAP_MS = 2000;
const FOCUS_WAKE_GAP_MS = 30_000;

interface WebSocketApi {
  sendMessage: (event: string, payload: unknown) => void;
  subscribe: (conversationId: string) => void;
  unsubscribe: (conversationId: string) => void;
  sendTyping: (conversationId: string, isTyping: boolean) => void;
  /** Raw event stream for subsystems (e.g. call signaling). Returns an unsubscribe. */
  onEvent: (handler: WsEventHandler) => () => void;
  /** Increments on every successful (re)connect; call state resets when it changes. */
  connectionEpoch: number;
  /**
   * The app woke (issue #75): fetch what's on screen now, without waiting
   * for the socket. Returns an unsubscribe.
   */
  onWake: (handler: WakeHandler) => () => void;
}

const WebSocketContext = createContext<WebSocketApi | null>(null);

// One connection for the whole app. Chat and the family apps all consume the
// same socket, and the subscription set is replayed on reconnect.
function useWebSocketConnection(): WebSocketApi {
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const subscribedRef = useRef<Set<string>>(new Set());
  const disposedRef = useRef(false);

  const accessToken = useAtomValue(accessTokenAtom);
  const addMessage = useSetAtom(addMessageAtom);
  const removeMessages = useSetAtom(removeMessagesAtom);
  const setTyping = useSetAtom(setTypingAtom);
  const setUserOnline = useSetAtom(setUserOnlineAtom);
  const applyAppEvent = useSetAtom(applyFamilyAppEventAtom);
  const setGatheringRing = useSetAtom(gatheringRingAtom);
  const eventHandlersRef = useRef<Set<WsEventHandler>>(new Set());
  const [connectionEpoch, setConnectionEpoch] = useState(0);
  const wakeHandlersRef = useRef<Set<WakeHandler>>(new Set());
  // The wake's health:ping waiting for its pong (issue #75).
  const pingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastWakeRef = useRef(0);

  const handleMessage = useCallback(
    (data: WsMessage) => {
      const { event, payload } = data;

      // Fan the raw event out first so call signaling (and any future
      // subsystem) sees it alongside the built-in chat handling.
      eventHandlersRef.current.forEach((handler) => {
        try {
          handler(data);
        } catch (err) {
          console.error('WebSocket event handler error:', err);
        }
      });

      switch (event) {
        case 'auth:success':
          setConnectionEpoch((epoch) => epoch + 1);
          break;

        // Your own message from any of your devices, this one included
        // (D206): into the open chat like any other, deduped by id and
        // clientId. Only message:received alerts or counts unread.
        case WS_EVENTS.MESSAGE_SENT:
        case WS_EVENTS.MESSAGE_RECEIVED:
          addMessage({
            conversationId: (payload as { conversationId: string })
              .conversationId,
            message: payload as Parameters<typeof addMessage>[0]['message'],
          });
          break;

        case WS_EVENTS.USER_TYPING:
          setTyping({
            conversationId: (payload as { conversationId: string })
              .conversationId,
            userId: (payload as { userId: string }).userId,
            isTyping: (payload as { isTyping: boolean }).isTyping,
          });
          break;

        case WS_EVENTS.USER_ONLINE:
          setUserOnline({
            userId: (payload as { userId: string }).userId,
            online: true,
          });
          break;

        case WS_EVENTS.USER_OFFLINE:
          setUserOnline({
            userId: (payload as { userId: string }).userId,
            online: false,
          });
          break;

        case WS_EVENTS.MESSAGE_READ:
          // Read receipts are rendered from the message GET payload for now.
          break;

        // Disappearing messages swept by the server (D238): out of the live
        // store, and any queued edit or reaction for them is dropped.
        case WS_EVENTS.MESSAGE_DELETED: {
          const deleted = payload as WsMessageDeletedPayload;
          removeMessages({
            conversationId: deleted.conversationId,
            messageIds: deleted.messageIds,
          });
          discardForMessages(deleted.messageIds);
          break;
        }

        // "Come now" (M71, D286): the full-screen ring, in whichever chat
        // this tab has open.
        case WS_EVENTS.GATHERING_RING:
          setGatheringRing(payload as WsGatheringRingPayload);
          break;

        default:
          // Family-app events (D17) are applied idempotently by entity id.
          applyAppEvent({ event, payload });
      }
    },
    [
      addMessage,
      removeMessages,
      applyAppEvent,
      setGatheringRing,
      setTyping,
      setUserOnline,
    ]
  );
  const connect = useCallback(() => {
    if (!accessToken || disposedRef.current) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;

    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    socket.onopen = () => {
      socket.send(
        JSON.stringify({
          event: 'auth',
          payload: {
            token: accessToken,
            conversationIds: Array.from(subscribedRef.current),
          },
        })
      );
    };

    // A replaced socket (StrictMode remount, token refresh, reconnect) can
    // still deliver or close after its successor opened. Only the current
    // socket counts; otherwise a tab ends up with two live sockets and every
    // event is applied twice.
    const isCurrent = () => socketRef.current === socket;

    socket.onmessage = (event) => {
      if (!isCurrent()) return;
      // Anything from the server proves the socket is alive (issue #75).
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
        pingTimeoutRef.current = undefined;
      }
      let data: WsMessage;
      try {
        data = JSON.parse(event.data);
      } catch (err) {
        console.error('WebSocket message parse error:', err);
        return;
      }
      if (data.event === WS_EVENTS.HEALTH_PONG) return;
      // Deleted accounts are named in the reader's language (M51).
      localizeDeletedAccounts(data.payload);
      // Messages are opened (end-to-end encryption, M28) before anyone sees
      // them, in arrival order; everything else (calls, typing) goes straight
      // through so it's never held up behind a key fetch.
      if (
        wsTransform &&
        OPENED_EVENTS.some((prefix) => data.event.startsWith(prefix))
      ) {
        const transform = wsTransform;
        messageQueue = messageQueue
          .then(() => transform(data.payload))
          .catch(() => undefined)
          .then(() => {
            if (isCurrent()) handleMessage(data);
          });
      } else {
        handleMessage(data);
      }
    };

    socket.onclose = (event) => {
      if (disposedRef.current || !isCurrent()) return;
      // The account was deleted on another device (M51): any request now
      // fails to refresh, which signs this one out too.
      if (event.code === 4001) {
        void api.getMe().catch(() => undefined);
        return;
      }
      reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
    };

    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }, [accessToken, handleMessage]);

  const connectRef = useRef(connect);
  connectRef.current = connect;

  // Drop the current socket and open a new one now, not after the reconnect
  // delay. The old one is no longer current, so its late close is ignored.
  const reconnectNow = useCallback(() => {
    if (disposedRef.current) return;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }
    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current);
      pingTimeoutRef.current = undefined;
    }
    const old = socketRef.current;
    socketRef.current = null;
    try {
      old?.close();
    } catch {
      // Already gone.
    }
    connectRef.current();
  }, []);

  // The app came back (issue #75). An iPhone Home Screen app is frozen in
  // the background and its socket dies without a close event: readyState
  // still says OPEN, nothing arrives, and nothing reconnects until the user
  // leaves and comes back. So check the socket at once: not open, reconnect
  // now; open, ping it and reconnect if the pong doesn't come in 3 s. And,
  // independently, tell the screens to fetch what they show (onWake), so the
  // new message is there without waiting for the socket. Same rule as the
  // phone apps' wake (#70, docs/native-notifications.md).
  const wake = useCallback(
    (next: Wake) => {
      if (disposedRef.current || !accessToken) return;
      const socket = socketRef.current;
      if (
        !socket ||
        socket.readyState === WebSocket.CLOSING ||
        socket.readyState === WebSocket.CLOSED
      ) {
        reconnectNow();
      } else if (socket.readyState === WebSocket.CONNECTING) {
        // Give it the same 3 s: one started before a freeze can hang as
        // long as a dead open socket.
        if (!pingTimeoutRef.current) {
          pingTimeoutRef.current = setTimeout(() => {
            pingTimeoutRef.current = undefined;
            if (socketRef.current === socket) reconnectNow();
          }, WAKE_PING_TIMEOUT_MS);
        }
      } else if (!pingTimeoutRef.current) {
        try {
          socket.send(
            JSON.stringify({ event: WS_EVENTS.HEALTH_PING, payload: {} })
          );
          pingTimeoutRef.current = setTimeout(() => {
            pingTimeoutRef.current = undefined;
            if (socketRef.current === socket) reconnectNow();
          }, WAKE_PING_TIMEOUT_MS);
        } catch {
          reconnectNow();
        }
      }

      const now = Date.now();
      const gap =
        next.reason === 'notification'
          ? 0
          : next.reason === 'focus'
            ? FOCUS_WAKE_GAP_MS
            : WAKE_GAP_MS;
      if (now - lastWakeRef.current < gap) return;
      lastWakeRef.current = now;
      wakeHandlersRef.current.forEach((handler) => {
        try {
          handler(next);
        } catch (err) {
          console.error('Wake handler error:', err);
        }
      });
    },
    [accessToken, reconnectNow]
  );

  useEffect(() => {
    if (!accessToken) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') wake({ reason: 'visible' });
    };
    const onPageShow = (event: PageTransitionEvent) => {
      // A first load is not a wake; a page back from the bfcache is.
      if (event.persisted) wake({ reason: 'pageshow' });
    };
    const onFocus = () => wake({ reason: 'focus' });
    const onOnline = () => wake({ reason: 'online' });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    // A tapped notification, with Twinden already open (sw.js). Routing is
    // useNotificationOpens's; this only wakes the socket and the screens.
    const container =
      typeof navigator !== 'undefined' && 'serviceWorker' in navigator
        ? navigator.serviceWorker
        : null;
    const onWorkerMessage = (event: MessageEvent) => {
      const conversationId = notificationConversationId(
        event.data,
        window.location.origin
      );
      if (conversationId === undefined) return;
      wake({
        reason: 'notification',
        ...(conversationId ? { conversationId } : {}),
      });
    };
    container?.addEventListener('message', onWorkerMessage);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      container?.removeEventListener('message', onWorkerMessage);
    };
  }, [accessToken, wake]);

  const onWake = useCallback((handler: WakeHandler) => {
    wakeHandlersRef.current.add(handler);
    return () => {
      wakeHandlersRef.current.delete(handler);
    };
  }, []);

  const sendMessage = useCallback((event: string, payload: unknown) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          event,
          payload,
          timestamp: new Date().toISOString(),
        })
      );
    }
  }, []);

  const subscribe = useCallback(
    (conversationId: string) => {
      subscribedRef.current.add(conversationId);
      sendMessage('subscribe', { conversationId });
    },
    [sendMessage]
  );

  const unsubscribe = useCallback(
    (conversationId: string) => {
      subscribedRef.current.delete(conversationId);
      sendMessage('unsubscribe', { conversationId });
    },
    [sendMessage]
  );

  const sendTyping = useCallback(
    (conversationId: string, isTyping: boolean) => {
      sendMessage(WS_EVENTS.MESSAGE_TYPING, {
        conversationId,
        isTyping,
      });
    },
    [sendMessage]
  );

  const onEvent = useCallback((handler: WsEventHandler) => {
    eventHandlersRef.current.add(handler);
    return () => {
      eventHandlersRef.current.delete(handler);
    };
  }, []);

  useEffect(() => {
    disposedRef.current = false;
    if (accessToken) connect();

    return () => {
      disposedRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
        pingTimeoutRef.current = undefined;
      }
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [accessToken, connect]);

  return {
    sendMessage,
    subscribe,
    unsubscribe,
    sendTyping,
    onEvent,
    connectionEpoch,
    onWake,
  };
}

// M28: opens encrypted message payloads (see lib/e2ee/session.ts).
let wsTransform: ((payload: unknown) => Promise<unknown>) | null = null;
let messageQueue: Promise<void> = Promise.resolve();
// Events that can carry encrypted content: messages and family apps (M28).
const OPENED_EVENTS = ['message:', 'link:', 'document:', 'list:'];

export function setWebSocketTransform(
  transform: ((payload: unknown) => Promise<unknown>) | null
) {
  wsTransform = transform;
}

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const value = useWebSocketConnection();
  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket(): WebSocketApi {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used inside WebSocketProvider');
  }
  return context;
}
